import { track } from './client';
import { getTelemetryContext } from './context';
import { safely } from './safely';

const TARGET_SELECTOR =
  '[data-track], button, a, [role="button"], input[type="checkbox"], input[type="radio"]';
const MAX_LABEL_LENGTH = 40;

export interface ClickDescription {
  target: string;
  label?: string;
  id?: string;
  kind: string;
  checked?: boolean;
}

const isToggle = (el: Element): el is HTMLInputElement => {
  if (el.tagName !== 'INPUT') return false;
  const { type } = el as HTMLInputElement;
  return type === 'checkbox' || type === 'radio';
};

const kindOf = (el: Element): string => {
  if (isToggle(el)) {
    if (el.type === 'radio') return 'radio';
    return el.getAttribute('role') === 'switch' ? 'switch' : 'checkbox';
  }
  const tag = el.tagName.toLowerCase();
  if (tag === 'tr') return 'row';
  if (tag === 'a') return 'link';
  if (tag === 'button') return 'button';
  return el.getAttribute('role') || tag;
};

const truncate = (text: string) => text.slice(0, MAX_LABEL_LENGTH);

const labelOf = (el: Element): string | undefined => {
  const named = el.getAttribute('aria-label') || el.getAttribute('title');
  if (named) return truncate(named);
  // A row's text is customer data and an input's text is its value: neither leaves the browser.
  if (el.tagName === 'TR' || el.tagName === 'INPUT') return undefined;
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  return text ? truncate(text) : undefined;
};

/** What was clicked, in words safe to send; `undefined` when nothing trackable was hit. */
export function describeClick(
  el: Element | null | undefined,
): ClickDescription | undefined {
  const target = el?.closest?.(TARGET_SELECTOR);
  if (!target || target.closest('[data-track-ignore]')) return undefined;

  const kind = kindOf(target);
  const tracked = target.getAttribute('data-track');
  const id = target.getAttribute('data-track-id') || undefined;
  // A data-track name replaces the text: rows and cells carry customer names.
  const description: ClickDescription = tracked
    ? { target: tracked, id, kind }
    : {
        target: target.id ? `#${target.id}` : kind,
        label: labelOf(target),
        id,
        kind,
      };
  if (isToggle(target)) description.checked = target.checked;
  return description;
}

/** Capture phase, so a handler that stops propagation still leaves a trace. Returns the detach. */
export function attachClickTelemetry(doc: Document): () => void {
  const onClick = (event: Event) =>
    safely(() => {
      const description = describeClick(event.target as Element | null);
      if (!description) return;
      const { page, modal } = getTelemetryContext();
      track('ui_click', { ...description, page, modal });
    });
  doc.addEventListener('click', onClick, true);
  return () => doc.removeEventListener('click', onClick, true);
}
